import * as THREE from 'three';
import { player, S } from './state.js';
import { stepPhysics, movementEffects, STAMINA_MAX, HURT_SLOW } from './player.js';
import { sendInput, onMessage, OP, unpackPos, unpackEye, unpackUnit, F_GROUND, F_BURNING } from './net.js';
import { remoteList } from './remote.js';
import { currentButtons } from './input.js';

// ============================================================
//  Client-side prediction + server reconciliation.
//
//  Задача: движение должно быть мгновенным, хотя правду говорит сервер.
//  Решение (классика Valve):
//
//   1. Игрок нажал W → клиент СРАЗУ двигает себя сам и запоминает ввод
//      в истории под номером seq. Никакого ожидания ответа.
//   2. Сервер считает то же самое и присылает своё состояние + номер
//      последнего обработанного ввода (ack).
//   3. Клиент берёт серверную позицию как истину, выбрасывает из истории
//      всё подтверждённое и ПЕРЕИГРЫВАЕТ оставшиеся вводы поверх неё.
//      Результат — где игрок должен быть сейчас с точки зрения сервера.
//   4. Если результат совпал с предсказанным (обычно так) — не делаем ничего.
//      Если нет — забираем серверную версию, но разницу гасим плавно, а не
//      рывком: резкий телепорт камеры читается как «лаг», даже если он прав.
//
//  Симуляция идёт фиксированным шагом 60 Гц. Это принципиально: сервер
//  считает ровно те dt, которые мы прислали, поэтому локальный шаг и
//  отправленный кадр обязаны быть одним и тем же шагом. Иначе на 144 Гц
//  мониторе клиент насчитал бы 144 шага, а серверу отправил 60 — и позиции
//  разошлись бы на ровном месте.
// ============================================================

export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;

/** Неподтверждённые вводы: {seq, dt, buttons, yaw} */
const history = [];
const MAX_HISTORY = 240;          // 4 секунды — заведомо больше любого разумного пинга

/** Порог, ниже которого расхождение считаем шумом квантования и игнорируем.
 *  Позиции ездят по сети с точностью 1 см, поэтому меньше 3 см сверять бессмысленно. */
const IGNORE_ERR = 0.03;

/** Выше этого расхождения плавное сглаживание уже неуместно — телепорт
 *  (респавн, толчок гранатой, серьёзный рассинхрон). */
const SNAP_ERR = 2.0;

/** Визуальное смещение для сглаживания коррекции (гасится экспоненциально) */
export const predError = new THREE.Vector3();

/** Диагностика: последнее расхождение и счётчик коррекций — видно в HUD */
export const predStats = { err: 0, corrections: 0, replayed: 0 };

const _st = {
  pos: new THREE.Vector3(), vel: new THREE.Vector3(), eye: 1.7, onGround: true,
  // Выносливость, факт бега и окно «в меня попадают» тоже переигрываются:
  // от них зависит скорость, а значит позиция (см. player.js::stepPhysics).
  stamina: 1, sprinting: false, hurt: 0,
};

/** Позиции сетевых игроков — в них упираемся (и клиент, и сервер). */
function others() {
  const out = [];
  for (const r of remoteList().values()) {
    if (r.group.visible) out.push(r.group.position);
  }
  return out;
}

/**
 * Один шаг симуляции: отправляем ввод и тут же применяем его локально.
 * Возвращает флаги позы для камеры/оружия/HUD.
 */
export function simulateStep(dt) {
  const buttons = currentButtons();
  const seq = sendInput(buttons, S.yaw, S.pitch, dt, S.lean);
  const mv = stepPhysics(player, buttons, S.yaw, dt, others());
  // seq === 0 означает «связи нет» — тогда история не нужна, играем офлайн
  if (seq) {
    history.push({ seq, dt, buttons, yaw: S.yaw });
    if (history.length > MAX_HISTORY) history.shift();
  }
  movementEffects(mv, dt);
  return mv;
}

// ---- Reconciliation ----

onMessage((op, d) => {
  if (op !== OP.SELF) return;

  const ack = d.getUint32(1, true);
  _st.pos.set(
    unpackPos(d.getInt16(5, true)),
    unpackPos(d.getInt16(7, true)),
    unpackPos(d.getInt16(9, true)));
  _st.vel.set(
    unpackPos(d.getInt16(11, true)),
    unpackPos(d.getInt16(13, true)),
    unpackPos(d.getInt16(15, true)));
  _st.eye = unpackEye(d.getUint16(17, true));
  _st.onGround = (d.getUint8(19) & F_GROUND) !== 0;
  // Горим ли (протокол 18). Флаг чисто для картинки — урон уже приехал
  // кадрами OP_DAMAGE, — но без него догорание вне лужи необъяснимо.
  S.burning = (d.getUint8(19) & F_BURNING) !== 0;
  // Выносливость (протокол 17): байт 0..255 → доля 0..1.
  _st.stamina = unpackUnit(d.getUint8(21)) * STAMINA_MAX;
  // Остаток окна замедления от попаданий (протокол 19): байт 0..255 → секунды.
  // Берём с сервера, а не заводим свой таймер по OP_DAMAGE: попадание считает
  // он, и окно обязано кончиться у обоих в один и тот же кадр ввода.
  _st.hurt = unpackUnit(d.getUint8(22)) * HURT_SLOW;
  _st.sprinting = player.sprinting;

  // Выбрасываем вводы, которые сервер уже учёл
  while (history.length && history[0].seq <= ack) history.shift();

  // Переигрываем неподтверждённое поверх серверного состояния.
  // Это и есть «догнать себя»: сервер знает, где мы были ack-вводов назад,
  // а мы досчитываем оттуда всё, что он ещё не видел.
  const o = others();
  for (const h of history) stepPhysics(_st, h.buttons, h.yaw, h.dt, o);
  predStats.replayed = history.length;

  // ⚠️ Стамину забираем ВСЕГДА, до порога расхождения позиции: она не
  // «дрожит» от квантования (сервер шлёт её ровно так же, как считает),
  // а сглаживать её незачем — полоска на экране не камера. Оставь её
  // клиентской, и после каждого отброшенного бюджетом кадра ввода запас
  // на клиенте расходился бы с серверным, то есть игрок видел бы силы
  // там, где сервер уже перевёл его на шаг.
  player.stamina = _st.stamina;
  player.sprinting = _st.sprinting;
  // Замедление — по той же причине и тем же путём, что стамина: оно решает,
  // побежит ли игрок в следующем шаге, и «дрожать» от квантования не может.
  player.hurt = _st.hurt;

  const err = _st.pos.distanceTo(player.pos);
  predStats.err = err;
  if (err < IGNORE_ERR) return;         // предсказание совпало — обычный случай

  predStats.corrections++;
  if (err > SNAP_ERR) {
    // Респавн/телепорт: сглаживать нечего, показываем как есть
    predError.set(0, 0, 0);
  } else {
    // Копим разницу как визуальное смещение и гасим её за несколько кадров.
    // Позиция игрока при этом СРАЗУ становится серверной — то есть стреляем
    // и упираемся мы уже по правде, «врёт» только картинка, и то доли секунды.
    predError.add(player.pos).sub(_st.pos);
  }
  player.pos.copy(_st.pos);
  player.vel.copy(_st.vel);
  player.eye = _st.eye;
  player.onGround = _st.onGround;
});

/** Гасит визуальное смещение коррекции. Вызывать каждый кадр. */
export function decayPredError(dt) {
  if (predError.lengthSq() < 1e-8) { predError.set(0, 0, 0); return; }
  predError.multiplyScalar(Math.pow(0.001, dt));   // ≈ до нуля за ~0.4 с
}

/** Сброс истории — при разрыве связи или смерти, чтобы не переигрывать мусор. */
export function resetPrediction() {
  history.length = 0;
  predError.set(0, 0, 0);
  S.predReset = (S.predReset || 0) + 1;
}
